// Composes a branded, shareable natal-chart + fortune card on a canvas -> PNG blob.

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapLines(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawMoon(ctx, cx, cy, r, frac) {
  // decorative gold moon disc; a soft crescent shadow hints at the phase
  const g = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.1, cx, cy, r);
  g.addColorStop(0, "#F4E3B8");
  g.addColorStop(1, "#B98F3E");
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = g;
  ctx.fill();
  // shadow crescent: shift a dark disc; magnitude from phase (new=large, full=0)
  const dark = 1 - 2 * Math.abs((frac ?? 0.5) - 0.5); // 1 at full, 0 at new
  const shift = (1 - dark) * r * 1.9 * ((frac ?? 0.5) < 0.5 ? -1 : 1);
  ctx.globalCompositeOperation = "source-atop";
  ctx.beginPath();
  ctx.arc(cx + shift, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(5,5,7,0.82)";
  ctx.fill();
  ctx.restore();
  // ring
  ctx.beginPath();
  ctx.arc(cx, cy, r + 6, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(197,160,89,0.5)";
  ctx.lineWidth = 2;
  ctx.stroke();
}

export async function generateChartBlob({ chart, place, fortune }) {
  try {
    await document.fonts.ready;
  } catch (e) {}

  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  const c = chart.chart;
  const mp = (fortune && fortune.sky && fortune.sky.moon_phase) || c.moon_phase;
  const moonSign = (fortune && fortune.sky && fortune.sky.moon_sign) || c.moon_sign;

  // Background
  ctx.fillStyle = "#050505";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, 360, 40, W / 2, 360, 720);
  glow.addColorStop(0, "rgba(59,42,72,0.5)");
  glow.addColorStop(1, "rgba(5,5,5,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Overline
  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(197,160,89,0.85)";
  ctx.font = "400 22px Outfit, sans-serif";
  try { ctx.letterSpacing = "8px"; } catch (e) {}
  ctx.fillText("YOUR CHART & FORTUNE", W / 2, 108);
  try { ctx.letterSpacing = "0px"; } catch (e) {}

  // Moon emblem
  drawMoon(ctx, W / 2, 300, 118, mp && mp.cycle_fraction);
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = "300 26px Outfit, sans-serif";
  ctx.fillText(`${mp.phase_name} · ${moonSign}`, W / 2, 470);

  // Placements row
  const cols = [
    ["SUN", c.planets.Sun.glyph, c.sun_sign],
    ["MOON", c.planets.Moon.glyph, c.moon_sign],
    ["RISING", c.ascendant.glyph, c.ascendant.sign],
  ];
  const colW = W / 3;
  cols.forEach((col, i) => {
    const cx = colW * i + colW / 2;
    ctx.fillStyle = "rgba(197,160,89,0.75)";
    ctx.font = "400 20px Outfit, sans-serif";
    try { ctx.letterSpacing = "4px"; } catch (e) {}
    ctx.fillText(col[0], cx, 560);
    try { ctx.letterSpacing = "0px"; } catch (e) {}
    ctx.fillStyle = "#C5A059";
    ctx.font = '300 72px "Cormorant Garamond", serif';
    ctx.fillText(col[1], cx, 646);
    ctx.fillStyle = "rgba(255,255,255,0.8)";
    ctx.font = '300 34px "Cormorant Garamond", serif';
    ctx.fillText(col[2], cx, 700);
  });

  // Divider
  ctx.strokeStyle = "rgba(197,160,89,0.25)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(140, 760);
  ctx.lineTo(W - 140, 760);
  ctx.stroke();

  // Fortune headline
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font = 'italic 300 52px "Cormorant Garamond", serif';
  const headline = (fortune && fortune.headline) || "The heavens keep your counsel.";
  let ty = 840;
  for (const ln of wrapLines(ctx, headline, W - 200).slice(0, 3)) {
    ctx.fillText(ln, W / 2, ty);
    ty += 64;
  }

  // Overall / lunar counsel
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = "300 30px Outfit, sans-serif";
  const body = (fortune && (fortune.lunar_counsel || fortune.overall)) || "";
  ty += 24;
  for (const ln of wrapLines(ctx, body, W - 220).slice(0, 4)) {
    ctx.fillText(ln, W / 2, ty);
    ty += 44;
  }

  // Born line
  if (place) {
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = "300 22px Outfit, sans-serif";
    ctx.fillText(`Born ${chart.date} · ${chart.time} · ${place}`, W / 2, H - 150);
  }

  // Footer branding
  ctx.fillStyle = "rgba(197,160,89,0.9)";
  ctx.font = "400 22px Outfit, sans-serif";
  try { ctx.letterSpacing = "8px"; } catch (e) {}
  ctx.fillText("SYBILS SOCIETY", W / 2, H - 84);
  try { ctx.letterSpacing = "3px"; } catch (e) {}
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.font = "300 18px Outfit, sans-serif";
  ctx.fillText("THE RETURN OF THE MATRIARCHS", W / 2, H - 52);
  try { ctx.letterSpacing = "0px"; } catch (e) {}
  ctx.textAlign = "left";

  // Frame
  ctx.strokeStyle = "rgba(197,160,89,0.35)";
  ctx.lineWidth = 2;
  roundRect(ctx, 26, 26, W - 52, H - 52, 22);
  ctx.stroke();

  return await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
}
